import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTenant, setActiveTenantState] = useState(
    localStorage.getItem("activeTenantId") || null
  );

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.user) setUser(res.data.user);
    return res.data;
  }, []);

  const verify2FA = useCallback(async (tempToken, code) => {
    const res = await api.post("/auth/2fa/login-verify", { temp_token: tempToken, code });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  // Forced first-time 2FA enrollment at login (mandatory roles). Uses a scoped
  // setup token instead of a session — the real session is issued on enable.
  const setup2FAEnroll = useCallback(async (setupToken, method) => {
    const res = await api.post("/auth/2fa/setup", { method, setup_token: setupToken });
    return res.data;
  }, []);

  const enable2FAEnroll = useCallback(async (setupToken, method, code) => {
    const res = await api.post(`/auth/2fa/enable?method=${method}`, { code, setup_token: setupToken });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const selectAccount = useCallback(async (tempToken, userId) => {
    const res = await api.post("/auth/select-account", { temp_token: tempToken, user_id: userId });
    if (res.data.user) setUser(res.data.user);
    return res.data;
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    localStorage.removeItem("activeTenantId");
    setActiveTenantState(null);
    setUser(null);
  }, []);

  const impersonate = useCallback(async (userId) => {
    const res = await api.post(`/auth/impersonate/${userId}`);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const stopImpersonate = useCallback(async () => {
    const res = await api.post("/auth/stop-impersonate");
    setUser(res.data.user);
    return res.data.user;
  }, []);

  // Super admin switches which tenant they are managing.
  const switchTenant = useCallback(async (tenantId) => {
    if (tenantId) localStorage.setItem("activeTenantId", tenantId);
    else localStorage.removeItem("activeTenantId");
    setActiveTenantState(tenantId || null);
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  }, []);

  // Multi-tenant users (e.g. admins) hop to their account in another tenant without re-login,
  // UNLESS that account has 2FA enabled — then the server returns a pending 2FA challenge.
  const switchMyTenant = useCallback(async (userId) => {
    const res = await api.post(`/auth/switch-tenant/${userId}`);
    if (res.data?.twofa_required) return res.data; // caller must complete 2FA
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, verify2FA, setup2FAEnroll, enable2FAEnroll, selectAccount, refreshUser, logout, setUser, impersonate, stopImpersonate, activeTenant, switchTenant, switchMyTenant }),
    [user, loading, activeTenant, login, verify2FA, setup2FAEnroll, enable2FAEnroll, selectAccount, refreshUser, logout, impersonate, stopImpersonate, switchTenant, switchMyTenant]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
